// Learning Center — data model (dealer portal consumption side).
// Mirrors the public.learning_modules row shape and the admin-portal-v5 model 1:1 (one
// content schema, two renderers per the pitch). Query results are cast to these types.

// One source of truth for what a valid tier is — keeps this feature from ever disagreeing
// with the app-wide helpers (useCurrentTier / isValidTier).
import type { Tier } from '@utils/tiers';

export type { Tier };
export type Audience = 'admin' | 'dealer' | 'driver';
export type ModuleStatus = 'draft' | 'published' | 'archived';
export type EffectiveStatus = 'draft' | 'scheduled' | 'published' | 'archived' | 'expired';
export type DriverType = 'w2' | '1099' | 'lite';

export type ModuleFeature =
  | 'generic'
  | 'move'
  | 'mobile_service'
  | 'guest_ride'
  | 'appraisals'
  | 'appointments';

/** Features that represent a purchased product — hidden unless the rooftop enabled them. */
export const PRODUCT_FEATURES: ModuleFeature[] = ['mobile_service', 'guest_ride', 'appraisals', 'appointments'];

/**
 * A module summary — every field except the (potentially large) markdown `content` body.
 * The library/list query selects these; `content` travels only on the by-key reader query.
 */
export interface ModuleSummary {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  audiences: Audience[];
  feature: ModuleFeature;
  tiers: Tier[] | null;
  driver_types: DriverType[] | null; // null = all driver types
  hero_video_url?: string | null;
  thumbnail_url?: string | null;
  attachment_url?: string | null;
  begins_at: string;
  ends_at?: string | null;
  status: ModuleStatus;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
}

/** A full module: a summary plus its GitHub-flavored markdown `content` body. */
export interface LearningModule extends ModuleSummary {
  content: string; // single GitHub-flavored markdown document
}

/** Human label for a feature category. */
export const FEATURE_LABELS: Record<ModuleFeature, string> = {
  generic: 'General',
  move: 'Moves',
  mobile_service: 'Mobile Service',
  guest_ride: 'Guest Rides',
  appraisals: 'Appraisals',
  appointments: 'Appointments',
};
export const featureLabel = (f: ModuleFeature): string => FEATURE_LABELS[f] ?? f;

export const MODULE_FEATURES: ModuleFeature[] = [
  'generic', 'move', 'mobile_service', 'guest_ride', 'appraisals', 'appointments',
];
